import type { RpcTransport, RpcRequest, RpcResponse, SerializedError } from "./types";
import { consoleDev } from "../console-dev";

export interface ExposeRpcOptions {
  /**
   * Called when an RPC request is received, before it is handled.
   * Defaults to logging to consoleDev.
   */
  onRequest?: (method: string, params: unknown) => void;

  /**
   * Called when an error occurs while handling an RPC request.
   */
  onError?: (error: Error, method: string, params: unknown) => void;
}

/**
 * Expose an API object over an RPC transport.
 * Incoming "rpc:req" messages are dispatched to api[method](params).
 *
 * @param transport - The transport to listen on
 * @param api - The API object to expose (methods will be called with params)
 * @param options - Optional configuration
 *
 * @example
 * ```typescript
 * const api = {
 *   async getUser({ id }: { id: string }) {
 *     return { id, name: "Alice" };
 *   }
 * };
 *
 * exposeRpc(transport, api);
 * ```
 */
/**
 * Default request logger that logs method and params to consoleDev.
 */
function defaultOnRequest(method: string, params: unknown): void {
  consoleDev.log(`[RPC] ${method}`, params);
}

export function exposeRpc(
  transport: RpcTransport,
  api: object,
  options: ExposeRpcOptions = {}
): void {
  const { onRequest = defaultOnRequest, onError } = options;

  transport.onMessage(async (raw) => {
    // Ignore non-RPC messages
    if (!raw || typeof raw !== "object") return;
    const msg = raw as RpcRequest;
    if (msg.type !== "rpc:req") return;

    const { id, method, params } = msg;
    const base: Pick<RpcResponse, "type" | "id"> = { type: "rpc:res", id };

    // Log incoming request
    onRequest(method, params);

    try {
      const fn = (api as Record<string, unknown>)[method];
      if (typeof fn !== "function") {
        throw new Error(`Unknown RPC method: ${method}`);
      }

      // Use .call(api, ...) to preserve `this` binding for class methods
      const result = await fn.call(api, params);
      transport.send({ ...base, result } as RpcResponse);
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      onError?.(err, method, params);

      const serialized: SerializedError = {
        message: err.message,
        name: err.name,
        stack: err.stack,
      };
      transport.send({ ...base, error: serialized } as RpcResponse);
    }
  });
}
